import React from 'react';
import { AutoAwesome as CoachIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

import type { JotCoachGap, JotCoachProviderQuestion } from '../store/hooks';

import { GapItem } from './GapItem';
import { ProviderQuestionItem } from './ProviderQuestionItem';

interface SectionCoachSidebarProps {
  sectionName: string;
  gaps: JotCoachGap[];
  providerQuestions: JotCoachProviderQuestion[];
  sessionId?: string;
  noteId?: string;
  sectionId?: string;
  onApplyEdit?: (gap: JotCoachGap) => void;
  onSubmitAnswer?: (question: JotCoachProviderQuestion, answer: string) => void;
}

export const SectionCoachSidebar: React.FC<SectionCoachSidebarProps> = ({
  sectionName,
  gaps,
  providerQuestions,
  sessionId,
  noteId,
  sectionId,
  onApplyEdit,
  onSubmitAnswer,
}) => {
  // Combine gaps and questions into a single interwoven list
  // We'll create an array of items with their type
  const allItems = [
    ...gaps.map(gap => ({ type: 'gap' as const, data: gap })),
    ...providerQuestions.map(question => ({ type: 'question' as const, data: question })),
  ];

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        boxShadow: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header - Always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <CoachIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, display: 'block', fontSize: '0.75rem' }}
          >
            JotCoach
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.7rem',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sectionName}
          </Typography>
        </Box>
      </Box>

      {/* Content - Always expanded, scrollable */}
      <CardContent sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        <Stack spacing={1.5}>
          {allItems.map((item, idx) =>
            item.type === 'gap' ? (
              <GapItem
                key={`gap-${item.data.id}`}
                gap={item.data}
                sessionId={sessionId}
                noteId={noteId}
                sectionId={sectionId}
                onApplyEdit={onApplyEdit}
              />
            ) : (
              <ProviderQuestionItem
                key={`question-${idx}`}
                question={item.data}
                sessionId={sessionId}
                noteId={noteId}
                sectionId={sectionId}
                onSubmitAnswer={onSubmitAnswer}
              />
            ),
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
